import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, ReiskostenEigenZorg,} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type ReiskostenEigenZorgFormGroup = FormGroup<{
  extraReisKostenZiekteInvaliditeit: FormControl<number | null>;
  uitgavenOpenbaarVervoerEnOverigVervoer: FormControl<number | null>;
  aantalKilometersNaartArtsZiekenhuis: FormControl<number | null>;
  prijsPerKilometerInCenten: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-reiskosten-eigen-zorg',
  templateUrl: './reiskosten-eigen-zorg.component.html',
  styleUrls: ['./reiskosten-eigen-zorg.component.scss'],
  imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule],
})
export class ReiskostenEigenZorgComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  private year: any;

  public formGroup = new FormGroup({
    reiskostenEigenZorg: new FormArray<ReiskostenEigenZorgFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.year = data.year;
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.reiskostenEigenZorg || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.reiskostenEigenZorg.getRawValue().map((w) => ({
        extraReisKostenZiekteInvaliditeit: w.extraReisKostenZiekteInvaliditeit,
        uitgavenOpenbaarVervoerEnOverigVervoer: w.uitgavenOpenbaarVervoerEnOverigVervoer,
        aantalKilometersNaartArtsZiekenhuis: w.aantalKilometersNaartArtsZiekenhuis,
        prijsPerKilometerInCenten: w.prijsPerKilometerInCenten,
      })) as ReiskostenEigenZorg;

    this.changeSubscriptions.add(
      this.reiskostenEigenZorg.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(),
    );
  }

  get reiskostenEigenZorg(): FormArray {
    return this.formGroup.controls.reiskostenEigenZorg;
  }

  addRij(): void {
    this.reiskostenEigenZorg.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.reiskostenEigenZorg.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: ReiskostenEigenZorg): FormGroup {
    let prijsPerKm = 21;
    if (this.year === 2024 || 2025) {
      prijsPerKm = 23
    }
    if (this.year >= 2026) {
      prijsPerKm = 25
    }

    return new FormGroup({
      extraReisKostenZiekteInvaliditeit: new FormControl<number | null>(item?.extraReisKostenZiekteInvaliditeit ?? null),
      uitgavenOpenbaarVervoerEnOverigVervoer: new FormControl<number | null>(item?.uitgavenOpenbaarVervoerEnOverigVervoer ?? null),
      aantalKilometersNaartArtsZiekenhuis: new FormControl<number | null>(item?.aantalKilometersNaartArtsZiekenhuis ?? null),
      prijsPerKilometerInCenten: new FormControl<number | null>(prijsPerKm ?? null),
    });
  }

  private updateForm(data: ReiskostenEigenZorg[]): void {
    const lijst = data || [];
    const formArray = this.reiskostenEigenZorg;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const reiskostenEigenZorg = this.formGroup
      .getRawValue()
      .reiskostenEigenZorg.filter(
        (restant) =>
          restant.extraReisKostenZiekteInvaliditeit ||
          restant.uitgavenOpenbaarVervoerEnOverigVervoer ||
          restant.prijsPerKilometerInCenten ||
          restant.aantalKilometersNaartArtsZiekenhuis,
      )
      .map((r) => {
        const prijsPerKilometerInCenten =
          this.year != undefined &&
          this.year > 2024 &&
          r.aantalKilometersNaartArtsZiekenhuis != null &&
          r.aantalKilometersNaartArtsZiekenhuis > 0 ? 23 : r.prijsPerKilometerInCenten;

        return {
          extraReisKostenZiekteInvaliditeit: r.extraReisKostenZiekteInvaliditeit,
          uitgavenOpenbaarVervoerEnOverigVervoer: r.uitgavenOpenbaarVervoerEnOverigVervoer,
          aantalKilometersNaartArtsZiekenhuis: r.aantalKilometersNaartArtsZiekenhuis,
          prijsPerKilometerInCenten,
        } as ReiskostenEigenZorg;
      });

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte.persoonsgebondenAftrek,
            pgaSpecifiekeZorgkosten: {
              ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
              reiskostenEigenZorg: reiskostenEigenZorg,
            },
          },
        },
      } as IncomeTaxEntity;
    });
  }
}
